import React, { useCallback, useMemo, useState } from "react";
import { ratingTone } from "Components/Common/PerformanceRatingTone";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import BreadCrumb from "Components/Common/BreadCrumb";
import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableFilters, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import DeleteModal from "Components/Common/DeleteModal";
import BulkActionBar from "Components/Common/BulkActionBar";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import Permission from "Components/Common/Permission";
import TableContainer from "Components/Common/TableContainer";
import moment from "moment";
import Import from "./Import";
import AgencyFilter from "pages/Contracts/AgencyFilter";
import {
  useNavigate,
  useSearchParams,
  Link,
  useLocation,
} from "react-router-dom";
import { toast } from "react-toastify";
import {
  getPerformanceRatings,
  deletePerformanceRating,
} from "common/services/performance-rating.service";
import Permissions from "Components/Common/Permissions";
import { normalize } from "common/lib/normalize";
import AddPerformanceRating from "./AddPerformanceRating";
import UpdatePerformanceRating from "./UpdatePerformanceRating";
import RatingTypeFilter from "./RatingTypeFilter";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import {
  FilterDateRange,
  FilterLabel,
  FilterSearchBox,
} from "Components/Common/TableFilters";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  performanceRatingType: string;
  ratedBy: string;
  dateRange: DateRange;
}
// @ts-ignore
const TextWrapper: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return null;
  const shortenedText =
    value.length > 300 ? value.slice(0, 200) + "..." : value;
  return (
    <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
      {shortenedText}
    </div>
  );
};

const PerformanceRatings = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "Performance Ratings");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "updatedAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const fetchData = () => {
    setPage(1);
    setSorted([{ id: "updatedAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (data: any) => {
    setSelectedRecord(data);
    setIsUpdateModalOpen(true);
  };

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));
  const defaultFilterValue: FilterValue = {
    performanceRatingType: "",
    ratedBy: "",
    dateRange: {
      fromDate: null,
      toDate: null,
    },
  };
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const [apiParams, setApiParams] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultSort: [{ id: "updatedAt", desc: true }],
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.ratedBy) {
        andConditions.push({
          ratedBy: {
            id: {
              $eq: filterValue.ratedBy,
            },
          },
        });
      }
      if (filterValue?.performanceRatingType) {
        andConditions.push({
          performanceRatingType: {
            id: {
              $eq: filterValue.performanceRatingType,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        andConditions.push({
          $or: [
            {
              updatedAt: {
                $between: [
                  filterValue.dateRange.fromDate,
                  filterValue.dateRange.toDate,
                ],
              },
            },
          ],
        });
      }

      andConditions.push({
        $or: [
          {
            description: {
              $containsi: searchValue,
            },
          },
          {
            contractor: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: ["ratedBy", "performanceRatingType", "contractor"],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getPerformanceRatings(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue, filterValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deletePerformanceRating(selectedRecord.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Performance rating deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting contract");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deletePerformanceRating(id);
          })
        );
        setDeleting(false);
        toast.success("Performance ratings deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "performance-rating",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Contractor Name",
        accessorKey: "contractor.contractorName",
        enableColumnFilter: false,
        id: "contractor.contractorName",
        cell: (cell: any) => {
          return (
            <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
              <Link
                to={`/contractors/${cell.row.original?.contractor?.id}`}
                className="fw-medium link-primary"
              >
                {cell.getValue()}
              </Link>
            </div>
          );
        },
      },

      {
        header: "Rated By",
        accessorKey: "ratedBy.name",
        id: "ratedBy.name",
        enableColumnFilter: false,
      },
      {
        header: "Performance Rating",
        accessorKey: "performanceRatingType.name",
        enableColumnFilter: false,
        id: "performanceRatingType.name",
        enableSorting: false,
        cell: (cell: any) => {
          const rating = cell.getValue();
          const bgColor = ratingTone(cell.row.original?.performanceRatingType);
          return (
            <React.Fragment>
              {(rating && (
                <div className={`badge rounded-pill bg-${bgColor} mb-0 fs-11`}>
                  <i className="mdi mdi-star mb-0"></i> {rating || ""}
                </div>
              )) ||
                "Not Provided"}
            </React.Fragment>
          );
        },
      },

      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
        id: "description",
        cell: (cell: any) => {
          return <TextWrapper value={cell.getValue() || "Not Provided"} />;
        },
      },

      {
        header: "Date From",
        accessorKey: "from",
        id: "from",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return cell.getValue() ? <>{date}</> : "Not Provided";
        },
      },

      {
        header: "Date To",
        accessorKey: "to",
        id: "to",
        enableColumnFilter: false,
        cell: (cell: any) => {
          // if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return cell.getValue() ? <>{date}</> : "Not Provided";
        },
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const status = cell.getValue();
          return (
            <React.Fragment>
              {status === null ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  N/A
                </span>
              ) : status === true ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  Active
                </span>
              ) : status === false ? (
                <span className="badge bg-danger-subtle text-danger fs-11">
                  Not Active
                </span>
              ) : null}
            </React.Fragment>
          );
        },
      },

      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        id: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="contract" action="findOne">
                  <DropdownItem
                    href={`#`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/contractors/${rowData?.contractor?.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="contract" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({
                        id: rowData.id,
                        ...rowData,
                      });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="contract" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  document.title = "Performance Ratings | CIMS";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Perfomance Ratings"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <Card>
            <CardBody>
              <Row className="g-3 align-items-end">
                <Col xxl={4} lg={6}>
                  <FilterSearchBox
                    id="performance-rating-search"
                    placeholder="Search performance rating..."
                    value={searchValue}
                    onChange={(value: string) => {
                      setSearchValue(value);
                      setPage(1);
                    }}
                  />
                </Col>
                <Col xxl={3} lg={6}>
                  <FilterLabel htmlFor="ratingTypeFilter">
                    Performance Rating
                  </FilterLabel>
                  <RatingTypeFilter
                    onChange={(value) => {
                      setFilterValue({
                        ...filterValue,
                        performanceRatingType: value,
                      });
                      setPage(1);
                    }}
                    value={filterValue.performanceRatingType}
                  />
                </Col>
                <Col xxl={3} lg={6}>
                  <FilterDateRange
                    id="performance-rating-date-range"
                    value={filterValue?.dateRange}
                    onChange={(dateRange: any) => {
                      setFilterValue({ ...filterValue, dateRange });
                      setPage(1);
                    }}
                  />
                </Col>
                <Col xxl={2} lg={6}>
                  <FilterLabel htmlFor="agencyFilter">Agency</FilterLabel>
                  <AgencyFilter
                    onChange={(value) => {
                      setFilterValue({ ...filterValue, ratedBy: value });
                      setPage(1);
                    }}
                    value={filterValue.ratedBy}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={8}>
                  <h5 className="card-title mb-1">Performance Ratings</h5>
                  <p className="text-muted mb-0">
                    This is a list of performance ratings for contractors,
                    vendors, and implementing partners as assessed by various UN
                    agencies. Contractors with ratings of{" "}
                    <span className="text-danger">'Suspended'</span> or
                    <span className="text-danger">
                      {" "}
                      'Globally Suspended'
                    </span>{" "}
                    are flagged, highlighting those requiring further review or
                    attention.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <Permission resource="contract" action="create">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          showAddModalForm();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Rating
                      </Link>
                    </Permission>

                    <Permissions
                      checks={[
                        {
                          resource: "contract",
                          action: "import",
                        },
                        {
                          resource: "contract",
                          action: "export",
                        },
                      ]}
                    >
                      <Import apiParams={apiParams} onImported={fetchUpdatedData} />
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
              <AddPerformanceRating
                toggle={() => setIsAddModalOpen((state) => !state)}
                isModalOpen={isAddModalOpen}
                fetchData={fetchData}
              />
              <UpdatePerformanceRating
                toggle={() => setIsUpdateModalOpen((state) => !state)}
                isModalOpen={isUpdateModalOpen}
                selectedRecord={selectedRecord}
                fetchData={fetchUpdatedData}
              />
              <DeleteModal
                show={deleteModal}
                onDeleteClick={() => handleDelete()}
                onCloseClick={() => setDeleteModal(false)}
                loading={deleting}
              />
              <DeleteModal
                show={deleteMultipleModal}
                onDeleteClick={() => handleDeleteMultiple()}
                onCloseClick={() => setDeleteMultipleModal(false)}
                loading={deleting}
              />
              <Permissions
                checks={[{ resource: "contractor", action: "delete" }]}
              >
                <BulkActionBar
                  count={selectedCheckBox.length}
                  onClear={unCheckAll}
                  itemLabel="rating"
                >
                  <Button
                    color="danger"
                    size="sm"
                    className="material-shadow-none"
                    onClick={() => setDeleteMultipleModal(true)}
                  >
                    <i className="ri-delete-bin-line align-bottom me-1"></i>{" "}
                    Delete
                  </Button>
                </BulkActionBar>
              </Permissions>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PerformanceRatings;
